/* Dynamic giftwrap/protection reconciler. No fetch/XHR monkey-patching. */
(function (root, document) {
  'use strict';
  var Core = root.GBAddonPricingCore;
  if (!Core) return;

  var EVENTS = ['cart:refresh', 'cart:updated', 'cart:change', 'cart:added', 'cart:build', 'cartQuantityUpdated'];
  var state = null;
  var queue = null;
  var ownMutation = false;
  var pendingWriteCount = 0;
  var localIntentAttrs = {};
  var lastWriteFailed = false;
  var refreshTimer = null;
  var refreshNeedsRead = false;
  var lastDrawerStateNode = null;
  var protectionRemovalPending = null;
  // Checkout gate (x1 2026-09-22): a 429 / non-OK / network reply on the add-on sync gets ONE backed-off retry,
  // then checkout continues (fail-open) inside the budget instead of leaving a dead Checkout button.
  // Budget counts from the gate call; a slow phone spends ~0.3 s between the tap and this call.
  var CHECKOUT_RETRY_MS = 300, CHECKOUT_BUDGET_MS = 1400, CHECKOUT_HARD_CAP_MS = 8000;
  var lastFailureTransient = false;
  function transient(error) {
    if (!error || typeof error !== 'object') error = new Error(String(error || 'network error'));
    try { error.gbTransient = true; } catch (e) {}
    return error;
  }

  function finishOwnMutation() {
    // MutationObserver delivers records after this synchronous DOM patch.
    // Keep the guard until those records have been consumed.
    if (root.queueMicrotask) root.queueMicrotask(function () { ownMutation = false; });
    else Promise.resolve().then(function () { ownMutation = false; });
  }

  function asId(value) { return value == null ? '' : String(value); }
  function propertySignature(value) {
    var object = Array.isArray(value) ? Object.fromEntries(value.filter(function (entry) { return Array.isArray(entry) && entry.length === 2; })) : value || {};
    return JSON.stringify(Object.keys(object).sort().map(function (key) { return [key, object[key]]; }));
  }
  function array(value) { return Array.isArray(value) ? value : []; }
  function positiveRate(current) {
    try {
      var active = root.Shopify && root.Shopify.currency && root.Shopify.currency.active;
      if (current && current.currency && active && String(current.currency) !== String(active)) return null;
    } catch (e) {}
    var direct = Core.positiveRate(current && current.rate);
    if (direct) return direct;
    try { return Core.positiveRate(root.Shopify && root.Shopify.currency && root.Shopify.currency.rate); } catch (e) { return null; }
  }
  function activeCurrency(current) {
    try { return current.currency || (root.Shopify && root.Shopify.currency && root.Shopify.currency.active) || 'USD'; } catch (e) { return current.currency || 'USD'; }
  }
  function activeCountry(current) {
    var candidate = '';
    try { candidate = root.Shopify && root.Shopify.country; } catch (e) {}
    candidate = String(candidate || '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(candidate) ? candidate : '';
  }
  function money(cents, current) {
    if (cents == null) return '';
    var currency = activeCurrency(current);
    var locale = (root.Shopify && root.Shopify.locale) ? String(root.Shopify.locale).replace('_', '-') : 'en-US';
    try { return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(cents / 100); }
    catch (e) { return (cents / 100).toFixed(2) + ' ' + currency; }
  }
  function parseState() {
    var node = document.querySelector('[data-gb-cart-state] [data-gb-addon-state], [data-gb-addon-state-section] [data-gb-addon-state]');
    if (!node) return null;
    try { return JSON.parse(node.textContent || '{}'); } catch (e) { return null; }
  }
  function card() { return document.querySelector('[data-gb-addon-wrap-card]'); }
  function status(text, kind) {
    var node = card() && card().querySelector('[data-gb-addon-status]');
    if (!node) return;
    node.textContent = text || '';
    if (kind) node.setAttribute('data-state', kind); else node.removeAttribute('data-state');
  }
  function wrapMapping(current) {
    var w = current.wrap || {};
    return { defaultId: w.defaultVariantId, tiers: w.variants || {} };
  }
  function protectionMapping(current) {
    var p = current.protection || {};
    return { defaultId: p.defaultVariantId, tiers: p.variants || {} };
  }
  function allVariantIds(mapping) {
    var ids = [];
    Object.keys(mapping.variants || {}).forEach(function (key) {
      var row = mapping.variants[key]; if (row && row.variantId) ids.push(asId(row.variantId));
    });
    if (mapping.defaultVariantId) ids.push(asId(mapping.defaultVariantId));
    if (mapping.productId) ids.push(asId(mapping.productId));
    return ids;
  }
  function lineHasProduct(line, productIds, handle) {
    var productId = asId(line.productId || line.product_id);
    var variantId = asId(line.variantId || line.variant_id);
    return productIds.indexOf(productId) >= 0 || productIds.indexOf(variantId) >= 0 || String(line.handle || '').toLowerCase() === handle;
  }
  function hasProtectionLine(current) {
    var ids = allVariantIds(current.protection || {});
    return array(current.lines).some(function (line) { return lineHasProduct(line, ids, 'shipping-protection') && Number(line.quantity || 0) > 0; });
  }
  function hasWrapLine(current) {
    var ids = allVariantIds(current.wrap || {});
    return array(current.lines).some(function (line) { return lineHasProduct(line, ids, 'giftwrap') && Number(line.quantity || 0) > 0; });
  }
  function choices(current, forceProtection) {
    var attrs = current.attributes || {};
    var wrapChoice = attrs.gb_wrap_choice === 'off' ? 'off' : 'on';
    var protectionChoice;
    if (attrs.gb_protection_choice === 'off') protectionChoice = 'off';
    else if (forceProtection) protectionChoice = 'on';
    else protectionChoice = hasProtectionLine(current) ? 'on' : 'off';
    return { wrapChoice: wrapChoice, protectionChoice: protectionChoice };
  }
  function mergeCart(current, cart) {
    if (!cart || !Array.isArray(cart.items)) return current;
    var byKey = {};
    array(current.lines).forEach(function (line) { byKey[String(line.key)] = line; });
    var lines = cart.items.map(function (item) {
      var old = byKey[String(item.key)] || {};
      return Object.assign({}, old, {
        key: item.key,
        productId: item.product_id || old.productId,
        variantId: item.variant_id || old.variantId,
        handle: item.handle || old.handle,
        productType: item.product_type || old.productType,
        title: item.product_title || old.title,
        quantity: item.quantity,
        requiresShipping: item.requires_shipping,
        isGiftCard: item.gift_card,
        originalLinePrice: item.original_line_price,
        finalLinePrice: item.final_line_price,
        properties: item.properties || old.properties || {}
      });
    });
    return Object.assign({}, current, { lines: lines, attributes: cart.attributes || current.attributes || {} });
  }
  function fetchCart() {
    return root.fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (response) {
      if (!response.ok) throw new Error('cart read ' + response.status);
      return response.json();
    });
  }
  function parseStateHTML(html) {
    if (!html) return null;
    try {
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var node = parsed.querySelector('[data-gb-addon-state]');
      return node ? JSON.parse(node.textContent || '{}') : null;
    } catch (e) { return null; }
  }
  function fetchFreshState() {
    var path = (root.location && root.location.pathname) || '/';
    var separator = path.indexOf('?') >= 0 ? '&' : '?';
    return root.fetch(path + separator + 'section_id=gb-addon-state&_gb_addon_state=' + Date.now(), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } }).then(function (response) {
      if (!response.ok) throw transient(new Error('state read ' + response.status));
      return response.text();
    }, function (error) { throw transient(error); }).then(function (html) {
      var fresh = parseStateHTML(html);
      if (!fresh) throw new Error('state section missing');
      return fresh;
    });
  }
  function replaceStateSection(html) {
    if (!html) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = parsed.querySelector('[data-gb-addon-state-section]');
    var old = document.querySelector('[data-gb-addon-state-section]');
    if (fresh && old) old.replaceWith(fresh);
    return parseStateHTML(html);
  }
  function cartPageSectionId(domId) {
    var node = document.getElementById(domId);
    return node && node.getAttribute ? node.getAttribute('data-id') : null;
  }
  function sectionSource(html, selector) {
    if (!html) return null;
    try { return new DOMParser().parseFromString(html, 'text/html').querySelector(selector); }
    catch (e) { return null; }
  }
  function applyCartPageSections(sections) {
    if (!sections || !document.getElementById('main-cart-items')) return;
    var itemsId = cartPageSectionId('main-cart-items');
    var itemsSource = itemsId && sectionSource(sections[itemsId], '#main-cart-items');
    var itemsTarget = document.getElementById('main-cart-items');
    if (itemsSource && itemsTarget) itemsTarget.replaceWith(itemsSource);
    var footerId = cartPageSectionId('main-cart-footer');
    var footerSource = footerId && sectionSource(sections[footerId], '#main-cart-footer');
    var footerTarget = document.getElementById('main-cart-footer');
    if (footerSource && footerTarget) footerTarget.replaceWith(footerSource);
  }
  function sectionList() {
    var sections = ['cart-drawer', 'cart-icon-bubble', 'gb-addon-state'];
    var itemsId = cartPageSectionId('main-cart-items');
    var footerId = cartPageSectionId('main-cart-footer');
    if (itemsId) sections.push(itemsId);
    if (footerId) sections.push(footerId);
    return sections;
  }
  function applySections(data) {
    if (!data || !data.sections) return null;
    ownMutation = true;
    try {
      if (root.GBCart && typeof root.GBCart.applyDrawer === 'function' && data.sections['cart-drawer']) {
        try { root.GBCart.applyDrawer(data.sections); } catch (e) {}
      } else if (data.sections['cart-drawer']) {
        var parsed = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
        var fresh = parsed.querySelector('cart-drawer');
        var old = document.querySelector('cart-drawer');
        if (fresh && old) old.innerHTML = fresh.innerHTML;
      }
      applyCartPageSections(data.sections);
      return replaceStateSection(data.sections['gb-addon-state']);
    } finally { finishOwnMutation(); }
  }
  function render() {
    if (!state) return;
    var addonCard = card();
    if (!addonCard) return;
    var units = Core.countBlasterUnits(state.lines || []);
    var rate = positiveRate(state);
    var wrap = Core.quoteGiftwrap(units, wrapMapping(state), rate);
    addonCard.hidden = units <= 0;
    var checkbox = addonCard.querySelector('[data-gb-wrap-choice]');
    var attrChoice = localIntentAttrs.gb_wrap_choice || (state.attributes && state.attributes.gb_wrap_choice);
    if (checkbox) {
      var actualWrap = hasWrapLine(state);
      checkbox.checked = lastWriteFailed ? actualWrap : attrChoice !== 'off';
      checkbox.disabled = pendingWriteCount > 0 || units <= 0 || (wrap.status !== 'ready' && !actualWrap);
    }
    if (checkbox) {
      addonCard.setAttribute('data-selected', String(checkbox.checked));
      var toggle = addonCard.querySelector('[data-gb-wrap-switch]');
      if (toggle) {
        toggle.setAttribute('aria-checked', String(checkbox.checked));
        toggle.disabled = checkbox.disabled;
      }
    }
    var price = addonCard.querySelector('[data-gb-addon-price]');
    var selected = Core.selectedAddonCents(state.lines || [], allVariantIds(state.wrap || {}), 'giftwrap');
    if (price) price.textContent = selected != null ? money(selected, state) : (wrap.status === 'ready' ? money(wrap.amountCents, state) : 'Unavailable');
    if (wrap.status === 'rate-unavailable') status('Could not update your options. Please try again.', 'error');
    else if (wrap.status === 'variant-unavailable') status('Could not update your options. Please try again.', 'error');
    else if (wrap.status === 'none') status('', '');
    else if (!addonCard.querySelector('[data-gb-addon-status]').getAttribute('data-state')) status('', '');
  }
  function fingerprint(plan, attrs) { return JSON.stringify({ updates: plan.updates, attrs: attrs }); }
  function planFor(current, forceProtection, forcedAttrs, stampAttrs) {
    var rate = positiveRate(current);
    var attrs = Object.assign({}, current.attributes || {}, forcedAttrs || {});
    var choiceState = Object.assign({}, current, { attributes: attrs });
    var choicesNow = choices(choiceState, forceProtection);
    var wrapUnits = Core.countBlasterUnits(current.lines || []);
    var wrap = Core.quoteGiftwrap(wrapUnits, wrapMapping(current), rate);
    var protection = Core.quoteProtection(current.lines || [], protectionMapping(current), rate);
    var plan = Core.reconcileAddonLines({
      lines: current.lines || [], wrap: wrap, protection: protection,
      wrapChoice: choicesNow.wrapChoice, protectionChoice: choicesNow.protectionChoice,
      wrapProductIds: allVariantIds(current.wrap || {}), protectionProductIds: allVariantIds(current.protection || {})
    });
    var updates = {};
    Object.keys(plan.updates).forEach(function (key) {
      var value = plan.updates[key];
      if (key.indexOf('variant:') === 0) updates[key.slice(8)] = value; else updates[key] = value;
    });
    if (forcedAttrs && stampAttrs) {
      attrs.gb_wrap_choice = forcedAttrs.gb_wrap_choice || attrs.gb_wrap_choice || 'on';
      attrs.gb_addon_pricing = 'v1';
      attrs.gb_addon_currency = activeCurrency(current);
      attrs.gb_addon_rate = String(rate || '');
      var country = activeCountry(current);
      attrs.gb_addon_country = country || '';
      if (forcedAttrs.gb_protection_choice) attrs.gb_protection_choice = forcedAttrs.gb_protection_choice;
    }
    if (wrapUnits > 0 || attrs.gb_wrap_last_units != null || hasWrapLine(current)) {
      attrs.gb_wrap_last_units = String(wrapUnits);
      attrs.gb_wrap_choice = choicesNow.wrapChoice;
    }
    var changedAttrs = {};
    Object.keys(attrs).forEach(function (key) { if ((current.attributes || {})[key] !== attrs[key] && (key === 'gb_wrap_choice' || key === 'gb_wrap_last_units' || key === 'gb_protection_choice' || key === 'gb_addon_pricing' || key === 'gb_addon_currency' || key === 'gb_addon_rate' || key === 'gb_addon_country')) changedAttrs[key] = attrs[key]; });
    var attributePayload = Object.keys(changedAttrs).length ? changedAttrs : null;
    return { plan: plan, updates: updates, attributes: attributePayload, wrap: wrap, protection: protection, fingerprint: fingerprint({ updates: updates }, attributePayload), choices: choicesNow };
  }
  function setPendingStatus(text, kind) { status(text, kind); }
  function validateReadback(result, freshState) {
    if (!freshState) return false;
    var attrs = freshState.attributes || {};
    if (result.attributes) {
      var attrKeys = Object.keys(result.attributes);
      for (var i = 0; i < attrKeys.length; i += 1) if (attrs[attrKeys[i]] !== result.attributes[attrKeys[i]]) return false;
    }
    var lines = freshState.lines || [];
    var keys = Object.keys(result.updates || {});
    for (var j = 0; j < keys.length; j += 1) {
      var key = keys[j], wanted = Number(result.updates[key]);
      var present = lines.some(function (line) { return String(line.key) === String(key) || String(line.variantId || line.variant_id) === String(key) || String(line.productId || line.product_id) === String(key); });
      var line = lines.find(function (item) { return String(item.key) === String(key) || String(item.variantId || item.variant_id) === String(key) || String(item.productId || item.product_id) === String(key); });
      var actual = line ? Number(line.quantity || 0) : 0;
      if (wanted > 0 && (!present || actual !== wanted)) return false;
      if (wanted === 0 && present && actual > 0) return false;
    }
    return true;
  }
  function applyWrite(result, depth) {
    depth = depth || 0;
    if (!result || (!Object.keys(result.updates).length && !result.attributes)) return Promise.resolve(true);
    return root.fetch('/cart/update.js?kaching_bundles=true', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: result.updates, attributes: result.attributes, sections: sectionList(), sections_url: root.location.pathname })
    }).then(function (response) {
      if (!response.ok) throw transient(new Error(response.status === 429 ? 'Too many cart updates. Try again.' : 'Cart update failed (' + response.status + ').'));
      return response.json();
    }, function (error) { throw transient(error); }).then(function (data) {
      var fresh = applySections(data);
      return (fresh ? Promise.resolve(fresh) : fetchFreshState()).then(function (freshState) {
        state = freshState || state;
        init();
        if (!validateReadback(result, freshState)) {
          throw new Error('cart readback mismatch');
        }
        var settledPlan = planFor(freshState, result.choices && result.choices.protectionChoice === 'on', null, false);
        if (Object.keys(settledPlan.updates).length) {
          if (depth >= 1) throw new Error('cart pricing did not settle');
          return applyWrite(settledPlan, depth + 1);
        }
        ownMutation = true;
        state = freshState || state;
        render();
        finishOwnMutation();
        lastWriteFailed = false;
        try { document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'gb-dynamic-addons' } })); } catch (e) {}
        return true;
      });
    }).catch(function (error) {
      lastFailureTransient = !!(error && error.gbTransient);
      try { if (root.console) root.console.warn('[GBAddon] cart update failed', error); } catch (e) {}
      lastWriteFailed = true;
      setPendingStatus('Could not update your options. Please try again.', 'error');
      return false;
    });
  }
  function perform(intent) {
    lastFailureTransient = false;
    if (root.GBCart && root.GBCart.addonState) state = root.GBCart.addonState() || state;
    var current = state;
    var previous = state;
    var fresh = intent && intent.forceFresh ? fetchFreshState().then(function (freshState) { current = freshState; return current; }) : Promise.resolve(current);
    return fresh.then(function (now) {
      state = now || state;
      if (!state) return false;
      render();
      var attrs = intent && intent.attributes ? Object.assign({}, localIntentAttrs, intent.attributes) : (Object.keys(localIntentAttrs).length ? Object.assign({}, localIntentAttrs) : null);
      var unitsNow = Core.countBlasterUnits(state.lines || []);
      var previouslyWrapped = Number((state.attributes || {}).gb_wrap_last_units || 0) > 0 || (previous && hasWrapLine(previous));
      if (unitsNow > 0 && !hasWrapLine(state) && previouslyWrapped && !(attrs && Object.prototype.hasOwnProperty.call(attrs, 'gb_wrap_choice'))) {
        attrs = Object.assign({}, attrs || {}, { gb_wrap_choice: 'off' });
      }
      var forceProtection = Boolean(intent && intent.forceProtection);
      if (intent && intent.checkout) {
        var currentAttrs = Object.assign({}, state.attributes || {}, attrs || {});
        var hasMerchandise = Core.merchandiseSubtotalCents(state.lines || []) > 0;
        var explicitProtectionOff = currentAttrs.gb_protection_choice === 'off';
        forceProtection = hasMerchandise && !explicitProtectionOff;
        attrs = Object.assign({}, attrs || {}, { gb_wrap_choice: currentAttrs.gb_wrap_choice === 'off' ? 'off' : 'on' });
        if (hasMerchandise || explicitProtectionOff) attrs.gb_protection_choice = forceProtection ? 'on' : 'off';
      }
      var result = planFor(state, forceProtection, attrs, Boolean(intent && intent.checkout));
      var wrapNeedsNew = result.plan.wrapOn && !hasWrapLine(state);
      var protectionNeedsNew = result.plan.protectionOn && !hasProtectionLine(state);
      var wrapExplicitOff = attrs && attrs.gb_wrap_choice === 'off';
      var protectionExplicitOff = attrs && attrs.gb_protection_choice === 'off';
      if (intent && intent.checkout && !wrapExplicitOff && result.wrap.status !== 'ready' && result.wrap.status !== 'none') { setPendingStatus('Could not update your options. Please try again.', 'error'); return false; }
      if (intent && intent.checkout && !protectionExplicitOff && result.protection.status !== 'ready' && result.protection.status !== 'no-merchandise') { setPendingStatus('Could not update your options. Please try again.', 'error'); return false; }
      if (result.wrap.status === 'rate-unavailable' && wrapNeedsNew) { setPendingStatus('Could not update your options. Please try again.', 'error'); return false; }
      if (result.protection.status === 'rate-unavailable' && protectionNeedsNew) { setPendingStatus('Could not update your options. Please try again.', 'error'); return false; }
      if (result.wrap.status === 'variant-unavailable' && wrapNeedsNew) { setPendingStatus('Could not update your options. Please try again.', 'error'); return false; }
      if (result.protection.status === 'variant-unavailable' && protectionNeedsNew) { setPendingStatus('Could not update your options. Please try again.', 'error'); return false; }
      return applyWrite(result);
    }).catch(function (error) {
      lastWriteFailed = true;
      lastFailureTransient = !!(error && error.gbTransient);
      try { if (root.console) root.console.warn('[GBAddon] fresh state failed', error); } catch (e) {}
      setPendingStatus('Could not update your options. Please try again.', 'error');
      return false;
    });
  }
  function enqueue(intent) {
    if (intent && intent.attributes) localIntentAttrs = Object.assign({}, localIntentAttrs, intent.attributes);
    pendingWriteCount += 1;
    status('', 'pending');
    render();
    var result = queue ? queue.enqueue(intent) : Promise.resolve(false);
    return result.then(function (value) {
      pendingWriteCount = Math.max(0, pendingWriteCount - 1);
      if (value === true && pendingWriteCount === 0) {
        lastWriteFailed = false;
        status('', '');
        Object.keys(localIntentAttrs).forEach(function (key) {
          if (state && state.attributes && state.attributes[key] === localIntentAttrs[key]) delete localIntentAttrs[key];
        });
      }
      render();
      return value;
    });
  }
  function onWrapChoice(event) {
    var value = event.target && event.target.checked ? 'on' : 'off';
    enqueue({ attributes: { gb_wrap_choice: value } });
  }
  function init() {
    if (!state) state = parseState() || state;
    var currentCard = card();
    if (currentCard && !currentCard.__gbAddonBound) {
      currentCard.__gbAddonBound = true;
      var checkbox = currentCard.querySelector('[data-gb-wrap-choice]');
      if (checkbox) {
        checkbox.addEventListener('change', onWrapChoice);
        currentCard.addEventListener('click', function (event) {
          if (!event.target.closest('.upsell-toggle-btn, [data-gb-wrap-switch]') || event.target.closest('a, input') || checkbox.disabled) return;
          event.preventDefault();
          checkbox.checked = !checkbox.checked;
          onWrapChoice({ target: checkbox });
        });
      }
    }
    render();
  }

  function protectionRemovalDomIdentity(target) {
    if (!target || !target.closest) return null;
    var removeControl = target.closest('cart-remove-button');
    var minusControl = target.closest('quantity-input button[name="minus"]');
    if (!removeControl && !minusControl) return null;
    var carrier = removeControl || (minusControl && minusControl.closest('quantity-input'));
    var row = target.closest('.cart-item') || target.closest('li') || target.closest('tr');
    var input = row && row.querySelector
      ? row.querySelector('input.quantity__input[data-quantity-variant-id], input.quantity__input')
      : null;
    if (!input && carrier && carrier.querySelector) {
      input = carrier.querySelector('input.quantity__input[data-quantity-variant-id], input.quantity__input');
    }
    var indexValue = input && input.getAttribute('data-index');
    if (!indexValue && carrier) indexValue = carrier.getAttribute('data-index');
    var index = Number(indexValue);
    var variantId = input && input.getAttribute('data-quantity-variant-id');
    var handle = removeControl && String(removeControl.getAttribute('data-handle') || '').toLowerCase();
    return {
      removeControl: removeControl,
      minusControl: minusControl,
      boundControl: removeControl || minusControl,
      index: Number.isInteger(index) && index > 0 ? index : null,
      variantId: variantId ? String(variantId) : '',
      handle: handle,
    };
  }

  function sameProtectionRemoval(a, b) {
    if (!a || !b) return false;
    return a.lineKey === b.lineKey && a.variantId === b.variantId && a.index === b.index;
  }

  function protectionRemovalLine(target) {
    if (!state) return null;
    var dom = protectionRemovalDomIdentity(target);
    if (!dom || !dom.index) return null;
    var line = state.lines && state.lines[dom.index - 1];
    var protectionIds = allVariantIds(state.protection || {});
    if (!line || !lineHasProduct(line, protectionIds, 'shipping-protection') || Number(line.quantity || 0) <= 0) return null;
    var lineVariantId = asId(line.variantId || line.variant_id);
    var variantMatches = !dom.variantId || dom.variantId === lineVariantId;
    var handleMatches = dom.handle === 'shipping-protection' && lineHasProduct(line, protectionIds, 'shipping-protection');
    // The DOM identity and the current canonical line must agree. This avoids
    // deleting protection when a stale index now points at another product.
    if (dom.handle && dom.handle !== 'shipping-protection') return null;
    if (!variantMatches || !handleMatches && !dom.variantId) return null;
    if (dom.minusControl && Number(line.quantity || 0) > 1) return null;
    return {
      line: line,
      identity: {lineKey: asId(line.key || line.id), variantId: lineVariantId, index: dom.index},
    };
  }

  function interceptProtectionRemoval(event) {
    var dom = protectionRemovalDomIdentity(event && event.target);
    if (!dom) return;
    if (protectionRemovalPending && (protectionRemovalPending.control === dom.boundControl || (
      protectionRemovalPending.variantId === dom.variantId && protectionRemovalPending.index === dom.index
    ))) {
      if (event.cancelable !== false) event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
      // Keep the binding through touch/pointer handoff events. The synthesized
      // click is the terminal event; clearing earlier lets a settled state make
      // that old click look unrelated and reach the native remover.
      if (event.type === 'click') protectionRemovalPending = null;
      return;
    }
    var candidate = protectionRemovalLine(event.target);
    if (!candidate) {
      return;
    }
    protectionRemovalPending = Object.assign({}, candidate.identity, {control: dom.boundControl});
    if (event.cancelable !== false) event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();
    Promise.resolve(enqueue({ attributes: { gb_protection_choice: 'off' } })).then(function (result) {
      if (!result && protectionRemovalPending && sameProtectionRemoval(candidate.identity, protectionRemovalPending)) protectionRemovalPending = null;
    });
  }
  function drawerStateChanged() {
    var node = document.querySelector('[data-gb-cart-state] [data-gb-addon-state]');
    if (!node || node === lastDrawerStateNode) return false;
    lastDrawerStateNode = node;
    try {
      var fresh = JSON.parse(node.textContent || '{}');
      if (!Array.isArray(fresh.lines)) return false;
      state = fresh;
      return true;
    } catch (e) { return false; }
  }
  function snapshotMatchesState(cart) {
    if (!cart || !state || !Array.isArray(cart.items) || !Array.isArray(state.lines) || cart.items.length !== state.lines.length || Number(cart.total_price) !== Number(state.totalPrice)) return false;
    if (JSON.stringify(cart.attributes || {}) !== JSON.stringify(state.attributes || {})) return false;
    return cart.items.every(function (item) { return state.lines.some(function (line) {
      var selectedPlan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
      return String(line.key) === String(item.key) && String(line.variantId) === String(item.variant_id) && Number(line.quantity) === Number(item.quantity)
        && Number(line.originalLinePrice) === Number(item.original_line_price)
        && Number(line.finalLinePrice) === Number(item.final_line_price)
        && propertySignature(line.properties) === propertySignature(item.properties)
        && String(line.sellingPlanId || '') === String(selectedPlan && selectedPlan.id || '')
        && JSON.stringify(line.discountAllocations || []) === JSON.stringify((item.discount_allocations || item.line_level_discount_allocations || []).map(function (allocation) { return allocation.amount; }));
    }); });
  }
  function scheduleRefresh(needsRead) {
    refreshNeedsRead = refreshNeedsRead || needsRead;
    if (refreshTimer != null) return;
    // Batch notifications from the same native cart render into one update.
    refreshTimer = root.setTimeout(function () {
      refreshTimer = null;
      var freshFromDrawer = drawerStateChanged();
      var forceFresh = refreshNeedsRead && !freshFromDrawer;
      refreshNeedsRead = false;
      init();
      enqueue({ forceFresh: forceFresh });
    }, 0);
  }
  EVENTS.forEach(function (name) { document.addEventListener(name, function (event) {
    if (ownMutation || (event && event.detail && event.detail.source === 'gb-dynamic-addons')) return;
    if (event && event.detail && snapshotMatchesState(event.detail.cart)) return;
    scheduleRefresh(true);
  }); });
  document.addEventListener('click', interceptProtectionRemoval, true);
  document.addEventListener('touchend', interceptProtectionRemoval, { capture: true, passive: false });
  document.addEventListener('pointerup', interceptProtectionRemoval, { capture: true, passive: false });
  document.addEventListener('DOMContentLoaded', init);
  window.addEventListener('pageshow', function (event) { init(); if (event.persisted) scheduleRefresh(true); });
  try {
    var rootNode = document.querySelector('cart-drawer');
    if (rootNode) new MutationObserver(function () {
      var node = document.querySelector('[data-gb-cart-state] [data-gb-addon-state]');
      if (ownMutation) { lastDrawerStateNode = node; return; }
      // Prices, spinners and status text never trigger another network read.
      if (!node || node === lastDrawerStateNode) return;
      scheduleRefresh(false);
    }).observe(rootNode, { childList: true, subtree: true });
  } catch (e) {}

  queue = Core.createSerializedQueue(function (intent) {
    return root.GBCart && root.GBCart.queueMutation ? root.GBCart.queueMutation(function () { return perform(intent); }) : perform(intent);
  });
  root.GBDynamicAddons = {
    buildId: 'gb-addon-runtime-r8',
    reconcile: function () { return enqueue({}); },
    beforeCheckout: function () {
      var started = Date.now(), done = false;
      return new Promise(function (resolve) {
        function finish(value) { if (!done) { done = true; resolve(value); } }
        function failOpen(reason) {
          if (done) return;
          try { if (root.console) root.console.warn('[GBAddon] add-on sync unavailable (' + reason + '); continuing to checkout'); } catch (e) {}
          finish(true);
        }
        function attempt(retried) {
          enqueue({ forceFresh: true, checkout: true }).then(function (ok) {
            // Read the failure kind in this continuation: the serialized queue starts no other run before it.
            var wasTransient = ok === false && lastFailureTransient;
            if (done) return;
            if (!wasTransient) { finish(ok); return; }
            if (retried) { failOpen('retry failed'); return; }
            root.setTimeout(function () { attempt(true); }, CHECKOUT_RETRY_MS);
            root.setTimeout(function () { failOpen('budget'); }, Math.max(0, started + CHECKOUT_BUDGET_MS - Date.now()));
          });
        }
        root.setTimeout(function () { failOpen('no reply'); }, CHECKOUT_HARD_CAP_MS);
        attempt(false);
      });
    }
  };
  lastDrawerStateNode = document.querySelector('[data-gb-cart-state] [data-gb-addon-state]');
  init();
  enqueue({});
})(window, document);
